// slice.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createSlice } from '@reduxjs/toolkit'
import { BreadcrumbSegment } from '../../components/BreadcrumbNavigation'
import { Product, SolutionSet, sortedProductsList } from '../../data/solutionSet'
import { RequestState } from '../common'
import {
  saveSolutionSet, getAvailableNCRSolutionSets, getAvailableSavedSolutionSets, removeSavedSolutionSet
} from './thunks'
import { COMPANY_CHANGE, LOGOUT } from '../globalActions'

export interface SolutionSetsState {
  /** The solution sets that the user has saved for themselves */
  availableSavedSolutionSets: SolutionSet[];
  /** The solution sets that are available as a part of NCR's Approved Solution Sets */
  availableNCRSolutionSets: SolutionSet[];
  /** The selected solution set from the table displaying the availableNCRSolutionSets */
  selectedSolutionSet: SolutionSet | null;
  /** The Selected solution set that can be edited */
  editableSelectedSolutionSet: SolutionSet | null;
  /** The state of the NCR approved solution sets fetch request */
  fetchNCRApprovedSolutionSetRequestState: RequestState
  fetchSavedSolutionSetRequestState: RequestState
  /** The state of the remove request */
  removeRequestState: RequestState;
  /** The state of the save request */
  saveRequestState: RequestState;
  solutionSetBreadcrumbs: BreadcrumbSegment[]
}

const initialState: SolutionSetsState = {
  availableSavedSolutionSets: [],
  availableNCRSolutionSets: [],
  selectedSolutionSet: null,
  editableSelectedSolutionSet: null,
  fetchNCRApprovedSolutionSetRequestState: 'idle',
  fetchSavedSolutionSetRequestState: 'idle',
  removeRequestState: 'idle',
  saveRequestState: 'idle',
  solutionSetBreadcrumbs: []
}

export const solutionSetSlice = createSlice({
  name: 'solutionSets',
  initialState,
  reducers: {
    setFetchNCRApprovedSolutionSetRequestState: (state, action) => {
      state.fetchNCRApprovedSolutionSetRequestState = action.payload
    },
    setEditableSelectedSolutionSet(state, action) {
      state.editableSelectedSolutionSet = action.payload
    },
    setSelectedSolutionSet(state, action) {
      state.selectedSolutionSet = action.payload
    },
    setFetchSavedSolutionSetsRequestState: (state, action) => {
      state.fetchSavedSolutionSetRequestState = action.payload
    },
    setSaveRequestState: (state, action) => {
      state.saveRequestState = action.payload
    },
    setRemoveRequestState: (state, action) => {
      state.removeRequestState = action.payload
    },
    setEditableSolutionSetBreadcrumbs: (state, action) => {
      state.solutionSetBreadcrumbs = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getAvailableNCRSolutionSets.pending, (state) => {
      state.fetchNCRApprovedSolutionSetRequestState = 'pending'
    })

    builder.addCase(getAvailableNCRSolutionSets.rejected, (state) => {
      state.fetchNCRApprovedSolutionSetRequestState = 'rejected'
      state.availableNCRSolutionSets = []
    })

    builder.addCase(getAvailableNCRSolutionSets.fulfilled, (state, action) => {
      state.availableNCRSolutionSets = action.payload
      state.fetchNCRApprovedSolutionSetRequestState = 'fulfilled'
    })

    builder.addCase(getAvailableSavedSolutionSets.pending, (state) => {
      state.fetchSavedSolutionSetRequestState = 'pending'
    })

    builder.addCase(getAvailableSavedSolutionSets.rejected, (state) => {
      state.fetchSavedSolutionSetRequestState = 'rejected'
      state.availableSavedSolutionSets = []
    })

    builder.addCase(getAvailableSavedSolutionSets.fulfilled, (state, action) => {
      const sortedavailableSolutionSets : SolutionSet[] = action.payload.map((solutionSet) => {
        const sorted = sortedProductsList.map((p) => solutionSet.products.find((v) => v.name === p)).filter((p): p is Product => !!p)
        return {
          ...solutionSet,
          products: sorted
        }
      })
      state.availableSavedSolutionSets = sortedavailableSolutionSets
      state.fetchSavedSolutionSetRequestState = 'fulfilled'
    })

    builder.addCase(saveSolutionSet.pending, (state) => {
      state.saveRequestState = 'pending'
    })

    builder.addCase(saveSolutionSet.rejected, (state) => {
      state.saveRequestState = 'rejected'
    })

    builder.addCase(saveSolutionSet.fulfilled, (state) => {
      state.saveRequestState = 'fulfilled'
    })

    builder.addCase(removeSavedSolutionSet.pending, (state) => {
      state.removeRequestState = 'pending'
    })

    builder.addCase(removeSavedSolutionSet.rejected, (state) => {
      state.removeRequestState = 'rejected'
    })

    builder.addCase(removeSavedSolutionSet.fulfilled, (state) => {
      state.removeRequestState = 'fulfilled'
    })

    // Reset state on logout
    builder.addCase(LOGOUT, () => initialState)
    builder.addCase(COMPANY_CHANGE, () => initialState)
  }
})

export default solutionSetSlice.reducer
export const {
  setFetchNCRApprovedSolutionSetRequestState,
  setEditableSelectedSolutionSet,
  setSelectedSolutionSet,
  setFetchSavedSolutionSetsRequestState,
  setSaveRequestState,
  setRemoveRequestState,
  setEditableSolutionSetBreadcrumbs
} = solutionSetSlice.actions
